import React, { useState } from "react";

// COMMOM Import
import PrimaryButton from "../../CommonButtons/PrimaryButton";

// UTLIS Import
import { linkIdentifier } from "../../../../utils/linkIdentifier";
import {
  GlobalConfiguration,
  IntervalConfiguration,
} from "../../../../utils/configurationProvider";

// Suggested prop reference for consumers -------------------------------------
/**
 * @typedef {((e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void)} OnClickEventHandler
 */
/**
 * @typedef {("button" | "submit")} BtnType
 */
/**
 * @typedef {("white" | "black")} HoverOption
 */
/**
 * @typedef {Object} ChipItem
 * @property {string} text - Chip label.
 * @property {boolean} selected - Highlight chip and render bullet list when true.
 * @property {boolean} [disabled] - Disable click/hover behaviour.
 * @property {HoverOption} [hoverOption] - Hover theme for enabled chips.
 * @property {string[]} [bulletPoint] - Optional bullet items shown under the chip when selected.
 * @property {(e: React.MouseEvent<HTMLDivElement>, index: number, chip: ChipItem) => void} [onClick] - Chip click handler.
 */
/**
 * @param {Object} props
 * @param {string} props.headerTitle - Main heading text displayed at the top.
 * @param {string} [props.headerFontWeight] - Heading font weight class (e.g. "normal", "semibold", "bold").
 * @param {string} [props.headerFontColor] - Heading text color class (e.g. "text-primary").
 * @param {string} [props.textSize] - Heading text size class (e.g. "font-md").
 * @param {string} [props.headerWithIcon] - Optional icon shown to the left of the heading.
 * @param {string} [props.subtext] - Supporting description text shown under the headers.
 * @param {boolean} [props.isalert] - Toggle alert banner visibility.
 * @param {string} [props.alertText] - Alert banner message.
 * @param {string} [props.alertImage] - Alert banner icon source.
 * @param {string} [props.alertBackground] - Alert banner background class (e.g. "emphasis").
 * @param {ChipItem[]} props.chipsArray - Interactive options rendered as chips.
 * @param {string} props.btnOneText - Primary (left) button label.
 * @param {BtnType} [props.btnOneBtnType] - Primary button type attribute.
 * @param {OnClickEventHandler} props.btnOneHandleFunction - Primary button click handler.
 * @param {boolean} [props.btnOneIsDisabled] - Disable primary button.
 * @param {string} [props.btnOneEnabledTextColor] - Primary button text color when enabled.
 * @param {string} [props.btnOneDisabledTextColor] - Primary button text color when disabled.
 * @param {string} [props.btnOneEnabledBgColor] - Primary button background when enabled.
 * @param {string} [props.btnOneDisabledBgColor] - Primary button background when disabled.
 * @param {string} [props.btnOneOutLineColor] - Primary button outline/border color.
 * @param {boolean} [props.btnOneFullWidthRequired] - Stretch primary button to full width.
 * @param {HoverOption} [props.btnOneHoverOption] - Primary button hover theme.
 * @param {boolean} [props.btnOneIsLoading] - Show loading state on primary button.
 * @param {React.RefObject<HTMLButtonElement>} [props.btnOneButtonRef] - Ref for the primary button.
 * @param {boolean} [props.btnOneInActiveVariantNeeded] - Render primary button inactive variant.
 * @param {boolean} [props.btnOneIsLoaderNeeded] - Render loader indicator inside primary button.
 * @param {string} props.btnTwoText - Secondary (right) button label.
 * @param {BtnType} [props.btnTwoBtnType] - Secondary button type attribute.
 * @param {OnClickEventHandler} props.btnTwoHandleFunction - Secondary button click handler.
 * @param {boolean} [props.btnTwoIsDisabled] - Disable secondary button.
 * @param {string} [props.btnTwoEnabledTextColor] - Secondary button text color when enabled.
 * @param {string} [props.btnTwoDisabledTextColor] - Secondary button text color when disabled.
 * @param {string} [props.btnTwoEnabledBgColor] - Secondary button background when enabled.
 * @param {string} [props.btnTwoDisabledBgColor] - Secondary button background when disabled.
 * @param {string} [props.btnTwoOutLineColor] - Secondary button outline/border color.
 * @param {boolean} [props.btnTwoFullWidthRequired] - Stretch secondary button to full width.
 * @param {HoverOption} [props.btnTwoHoverOption] - Secondary button hover theme.
 * @param {boolean} [props.btnTwoIsLoading] - Show loading state on secondary button.
 * @param {React.RefObject<HTMLButtonElement>} [props.btnTwoButtonRef] - Ref for the secondary button.
 * @param {boolean} [props.btnTwoInActiveVariantNeeded] - Render secondary button inactive variant.
 * @param {boolean} [props.btnTwoIsLoaderNeeded] - Render loader indicator inside secondary button.
 * @returns {JSX.Element}
 */
const EditCommonPopup = ({
  headerTitle,
  headerWithIcon,
  subtext,
  btnOneBtnType,
  btnOneDisabledTextColor,
  btnOneDisabledBgColor,
  btnOneIsLoading,
  btnOneButtonRef,
  btnOneInActiveVariantNeeded,
  btnOneIsLoaderNeeded,
  btnOneHandleFunction,
  btnOneEnabledBgColor,
  btnOneOutLineColor,
  btnOneEnabledTextColor,
  btnOneIsDisabled,
  btnOneHoverOption,
  btnOneFullWidthRequired,
  btnTwoText,
  btnTwoBtnType,
  btnTwoDisabledTextColor,
  btnTwoIsLoading,
  btnTwoButtonRef,
  btnTwoInActiveVariantNeeded,
  btnTwoIsLoaderNeeded,
  btnTwoHandleFunction,
  btnTwoEnabledBgColor,
  btnTwoDisabledBgColor,
  btnTwoOutLineColor,
  btnTwoEnabledTextColor,
  btnTwoIsDisabled,
  btnTwoFullWidthRequired,
  btnTwoHoverOption,
  isalert,
  alertText,
  alertImage,
  alertBackground,
  chipsArray,
}) => {
  // useState
  const [animate, setAnimate] = useState(false);

  const safeBtnOneHandler =
    typeof btnOneHandleFunction === "function"
      ? btnOneHandleFunction
      : () => {};
  const safeBtnTwoHandler =
    typeof btnTwoHandleFunction === "function"
      ? btnTwoHandleFunction
      : () => {};
  const safeChipsArray = Array.isArray(chipsArray) ? chipsArray : [];

  const toSentenceCase = (text = "") =>
    text.charAt(GlobalConfiguration?.lengthCheckZero).toUpperCase() +
    text.slice(GlobalConfiguration?.lengthCheckOne).toLowerCase();

  const needsFullStop = (text = "") => !text.trim().endsWith(".");

  return (
    <div
      className="flex w-full justify-center items-center m-auto top-1/2 left-1/2 fixed z-z-index-modal p-padding-sm"
      style={{ transform: "translate(-50%, -50%)" }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className={` max-h-small-popup-height w-full Qmd:w-small-popup-width flex flex-col  py-padding-md bg-primary-bg rounded-border-radius-md animate-none ${animate ? "Qmd:animate-slideInFromLeft" : "Qmd:animate-slideInFromRight"}`}
      >
        <div className="h-full flex flex-col overflow-hidden">
          <div
            className={` flex flex-col px-padding-md ${subtext ? "gap-gap-xs" : ""}`}
          >
            <div className="flex gap-gap-sm">
              <img
                src={headerWithIcon}
                alt=""
                className={`h-icon-md w-icon-md ${!headerWithIcon && "hidden"} `}
              />

              <p className={`text-text-primary   text-font-md  font-semibold `}>
                {toSentenceCase(headerTitle)}
              </p>
            </div>

            {subtext && (
              <span className="text-font-xs text-text-secondary font-normal ">
                {linkIdentifier(subtext)}
                {needsFullStop(subtext) && <span>.</span>}
              </span>
            )}
          </div>

          {isalert && (
            <div className="pt-padding-md px-padding-md">
              <div
                className={`bg-${alertBackground} flex items-center  rounded-border-radius-md gap-gap-sm p-padding-sm`}
              >
                <img
                  className="h-icon-md w-icon-md"
                  src={alertImage}
                  alt=""
                />
                {alertText && (
                  <p className={` text-text-primary text-font-xs`}>
                    {linkIdentifier(alertText)}
                    {needsFullStop(alertText) && <span>.</span>}
                  </p>
                )}
              </div>
            </div>
          )}

          <div
            className={` ${isalert ? "pt-padding-sm " : "mt-margin-md "} flex flex-col gap-gap-sm overflow-y-auto flex-1 px-padding-md h-max-height-top-slider`}
          >
            {safeChipsArray.map((item, index) => (
              <div
                key={index}
                className="flex flex-col gap-gap-xs "
              >
                <div
                  onClick={(e) => {
                    if (!item.disabled && item?.onClick) {
                      item?.onClick(e, index, item);
                    }
                  }}
                >
                  <p
                    className={`
${item.selected ? "bg-text-primary text-primary-card" : "text-text-primary bg-primary-nav "} 
    ${item.disabled ? "opacity-light-opacity cursor-not-allowed " : ""}  
    ${item.disabled ? "" : item.selected ? "" : item.hoverOption ? `global-hover-${item.hoverOption} cursor-pointer` : ""} 
    relative z-0 
    rounded-border-radius-md 
        p-padding-md 
      text-font-md  
    `}
                  >
                    {item.text}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <div
            className={`text-text-secondary text-font-xs font-normal px-padding-md ${!safeChipsArray.some((item) => item.selected && item.bulletPoint && Array.isArray(item.bulletPoint) && item.bulletPoint.length > 0) && "hidden"} ${safeChipsArray.some((item) => item.selected && item.bulletPoint && Array.isArray(item.bulletPoint) && item.bulletPoint.length > 0) && "pt-padding-md"}`}
          >
            {safeChipsArray.map(
              (item, index) =>
                item.selected &&
                item.bulletPoint &&
                Array.isArray(item.bulletPoint) &&
                item.bulletPoint.length >
                  GlobalConfiguration.lengthCheckZero && (
                  <div
                    key={index}
                    className={`${item?.bulletPoint?.length === GlobalConfiguration.lengthCheckOne ? "grid-cols-1" : "grid-cols-2"} list-disc grid gap-gap-xs pl-padding-md`}
                  >
                    {item.bulletPoint.map((bullet, bulletIndex) => (
                      <li
                        key={bulletIndex}
                        className="text-text-secondary text-font-sm font-normal"
                      >
                        <span>{linkIdentifier(bullet)}</span>
                      </li>
                    ))}
                  </div>
                ),
            )}
          </div>
          <div className="w-full gap-gap-sm px-padding-md flex justify-end pt-padding-md">
            <PrimaryButton
              btnText={"Cancel"}
              btnType={btnOneBtnType}
              handleFunction={() => {
                if (!btnOneIsDisabled) {
                  setAnimate(true);
                  setTimeout(() => {
                    safeBtnOneHandler();
                  }, IntervalConfiguration.intervalSemiMid);
                }
              }}
              isDisabled={btnOneIsDisabled}
              enabledTextColor={btnOneEnabledTextColor}
              disabledTextColor={btnOneDisabledTextColor}
              enabledBgColor={btnOneEnabledBgColor}
              disabledBgColor={btnOneDisabledBgColor}
              outLineColor={btnOneOutLineColor}
              fullWidthRequired={btnOneFullWidthRequired}
              hoverOption={btnOneHoverOption}
              isLoading={btnOneIsLoading}
              buttonRef={btnOneButtonRef}
              inActiveVariantNeeded={btnOneInActiveVariantNeeded}
              isLoaderNeeded={btnOneIsLoaderNeeded}
            />
            <PrimaryButton
              btnText={btnTwoText}
              handleFunction={() => {
                if (!btnTwoIsDisabled) {
                  setAnimate(true);
                  setTimeout(() => {
                    safeBtnTwoHandler();
                  }, IntervalConfiguration.intervalSemiMid);
                }
              }}
              enabledBgColor={btnTwoEnabledBgColor}
              outLineColor={btnTwoOutLineColor}
              enabledTextColor={btnTwoEnabledTextColor}
              isDisabled={btnTwoIsDisabled}
              fullWidthRequired={btnTwoFullWidthRequired}
              hoverOption={btnTwoHoverOption}
              btnType={btnTwoBtnType}
              disabledTextColor={btnTwoDisabledTextColor}
              disabledBgColor={btnTwoDisabledBgColor}
              isLoading={btnTwoIsLoading}
              buttonRef={btnTwoButtonRef}
              inActiveVariantNeeded={btnTwoInActiveVariantNeeded}
              isLoaderNeeded={btnTwoIsLoaderNeeded}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditCommonPopup;
